import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Search,
  Send,
  Users,
  User,
  Mail,
  X,
  Plus,
  FileText,
  BarChart3,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { useAdminAuth } from "@/context/Admin";
import Loader from "@/components/loader/loader";
import { format } from "date-fns";
import RichTextEditor from "@/components/ui/rich-text-editor";

const DEFAULT_MESSAGE = `<p>Hello {{name}},</p><p><br></p><p>Best regards,<br><strong>The Fayoz Team</strong></p>`;

export default function SendMail() {
  const {
    getAllUsers,
    sendBulkEmail,
    sendPreviewEmail,
    getEmailLogs,
    getEmailAnalytics,
  } = useAdminAuth();

  // Tab state
  const [activeTab, setActiveTab] = useState("compose");

  // Compose email state
  const [recipientType, setRecipientType] = useState("all");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [previewEmail, setPreviewEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);

  // Email logs state
  const [emailLogs, setEmailLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsTotalPages, setLogsTotalPages] = useState(0);
  const [logsFilters, setLogsFilters] = useState({ status: "all", email: "" });

  // Email analytics state
  const [analytics, setAnalytics] = useState([]);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Fetch users on component mount
  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const data = await getAllUsers(1, 1000, "active");
        const users = (data.users || []).map((user) => ({
          id: user.id,
          name:
            `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
            user.email,
          email: user.email,
          avatar: "/placeholder.svg?height=32&width=32",
        }));
        setAllUsers(users);
        setFilteredUsers(users);
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.error("Failed to load users.");
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [getAllUsers]);

  // Filter users based on search term
  useEffect(() => {
    const filtered = allUsers.filter(
      (user) =>
        user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email.toLowerCase().includes(searchTerm.toLowerCase()),
    );
    setFilteredUsers(filtered);
  }, [searchTerm, allUsers]);

  const handleUserSelection = (user, isSelected) => {
    if (isSelected) {
      setSelectedUsers((prev) => [...prev, user]);
    } else {
      setSelectedUsers((prev) => prev.filter((u) => u.id !== user.id));
    }
  };

  const removeSelectedUser = (userId) => {
    setSelectedUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  const selectAllUsers = () => {
    setSelectedUsers(filteredUsers);
  };

  const clearAllUsers = () => {
    setSelectedUsers([]);
  };

  const handleSendMail = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error("Please fill in both subject and message fields.");
      return;
    }

    if (recipientType === "selected" && selectedUsers.length === 0) {
      toast.error("Please select at least one user to send the email to.");
      return;
    }

    setIsLoading(true);

    try {
      let userIds = null;
      if (recipientType === "selected") {
        userIds = selectedUsers.map((user) => user.id);
      }

      const result = await sendBulkEmail(
        subject.trim(),
        message.trim(),
        userIds,
      );

      // Reset form on success
      setSubject("");
      setMessage(DEFAULT_MESSAGE);
      setSelectedUsers([]);
      setRecipientType("all");
    } catch (error) {
      console.error("Error sending email:", error);
      // Error toast is already shown by sendBulkEmail function
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendPreview = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error("Please fill in both subject and message fields.");
      return;
    }

    // Validate preview email if provided
    if (previewEmail.trim()) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(previewEmail.trim())) {
        toast.error("Please enter a valid email address for preview.");
        return;
      }
    }

    setIsLoading(true);
    try {
      await sendPreviewEmail(
        subject.trim(),
        message.trim(),
        [],
        previewEmail.trim() || null,
      );
    } catch (error) {
      console.error("Error sending preview email:", error);
      // Error toast is already shown by sendPreviewEmail function
    } finally {
      setIsLoading(false);
    }
  };

  const getRecipientCount = () => {
    if (recipientType === "all") return allUsers.length;
    if (recipientType === "selected") return selectedUsers.length;
    return 1;
  };

  // Fetch email logs
  const fetchEmailLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const filters = {};
      if (logsFilters.status && logsFilters.status !== "all")
        filters.status = logsFilters.status;
      if (logsFilters.email) filters.email = logsFilters.email;

      const response = await getEmailLogs(logsPage, 20, filters);
      setEmailLogs(response.data || []);
      setLogsTotal(response.total || 0);
      setLogsTotalPages(response.totalPages || 0);
    } catch (error) {
      console.error("Error fetching email logs:", error);
    } finally {
      setLogsLoading(false);
    }
  }, [getEmailLogs, logsPage, logsFilters]);

  // Fetch email analytics
  const fetchEmailAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const response = await getEmailAnalytics();
      setAnalytics(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching email analytics:", error);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [getEmailAnalytics]);

  // Fetch logs when tab changes or filters change
  useEffect(() => {
    if (activeTab === "logs") {
      fetchEmailLogs();
    }
  }, [activeTab, fetchEmailLogs]);

  // Fetch analytics when tab changes
  useEffect(() => {
    if (activeTab === "analytics") {
      fetchEmailAnalytics();
    }
  }, [activeTab, fetchEmailAnalytics]);

  // Refresh logs after sending email
  const handleSendMailWithRefresh = async () => {
    await handleSendMail();
    if (activeTab === "logs") {
      setTimeout(() => fetchEmailLogs(), 1000);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            Email Management
          </h1>
          <p className="text-muted-foreground">
            Compose emails, view logs, and analyze performance
          </p>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-6"
      >
        <TabsList>
          <TabsTrigger value="compose">
            <Mail className="h-4 w-4 mr-2" />
            Compose
          </TabsTrigger>
          <TabsTrigger value="logs">
            <FileText className="h-4 w-4 mr-2" />
            Logs
          </TabsTrigger>
          <TabsTrigger value="analytics">
            <BarChart3 className="h-4 w-4 mr-2" />
            Analytics
          </TabsTrigger>
        </TabsList>

        {/* Compose Tab */}
        <TabsContent value="compose" className="space-y-6">
          <div className="grid gap-6 lg:grid-cols-3">
            {/* Main Email Composition */}
            <div className="lg:col-span-2 space-y-6">
              {/* Recipient Selection */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="h-5 w-5" />
                    Recipients
                  </CardTitle>
                  <CardDescription>
                    Choose who will receive this email
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="all-users"
                        name="recipient-type"
                        value="all"
                        checked={recipientType === "all"}
                        onChange={(e) => setRecipientType(e.target.value)}
                        className="h-4 w-4"
                      />
                      <Label
                        htmlFor="all-users"
                        className="flex items-center gap-2"
                      >
                        <Users className="h-4 w-4" />
                        All Users ({allUsers.length})
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="radio"
                        id="selected-users"
                        name="recipient-type"
                        value="selected"
                        checked={recipientType === "selected"}
                        onChange={(e) => setRecipientType(e.target.value)}
                        className="h-4 w-4"
                      />
                      <Label
                        htmlFor="selected-users"
                        className="flex items-center gap-2"
                      >
                        <User className="h-4 w-4" />
                        Selected Users ({selectedUsers.length})
                      </Label>
                    </div>
                  </div>

                  {recipientType === "selected" && (
                    <div className="space-y-4">
                      {/* Selected Users Display */}
                      {selectedUsers.length > 0 && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm font-medium">
                              Selected Users:
                            </Label>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={clearAllUsers}
                            >
                              Clear All
                            </Button>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {selectedUsers.map((user) => (
                              <Badge
                                key={user.id}
                                variant="secondary"
                                className="flex items-center gap-1"
                              >
                                {user.name}
                                <button
                                  onClick={() => removeSelectedUser(user.id)}
                                  className="ml-1 hover:bg-destructive hover:text-destructive-foreground rounded-full p-0.5"
                                >
                                  <X className="h-3 w-3" />
                                </button>
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* User Search and Selection */}
                      <div className="space-y-3">
                        <div className="relative">
                          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Search users by name or email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-10"
                          />
                        </div>

                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={selectAllUsers}
                          >
                            <Plus className="h-4 w-4 mr-1" />
                            Select All
                          </Button>
                        </div>

                        <ScrollArea className="h-64 border rounded-md p-4">
                          <div className="space-y-2">
                            {loadingUsers ? (
                              <div className="flex items-center justify-center py-8">
                                <Loader />
                              </div>
                            ) : filteredUsers.length === 0 ? (
                              <div className="text-center text-gray-500 py-8">
                                No users found
                              </div>
                            ) : (
                              filteredUsers.map((user) => {
                                const isSelected = selectedUsers.some(
                                  (u) => u.id === user.id,
                                );
                                return (
                                  <div
                                    key={user.id}
                                    className="flex items-center space-x-3 p-2 hover:bg-muted rounded-md"
                                  >
                                    <Checkbox
                                      checked={isSelected}
                                      onCheckedChange={(checked) =>
                                        handleUserSelection(user, checked)
                                      }
                                    />
                                    <Avatar className="h-8 w-8">
                                      <AvatarImage
                                        src={user.avatar || "/placeholder.svg"}
                                        alt={user.name}
                                      />
                                      <AvatarFallback>
                                        {user.name.charAt(0)}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium truncate">
                                        {user.name}
                                      </p>
                                      <p className="text-xs text-muted-foreground truncate">
                                        {user.email}
                                      </p>
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </ScrollArea>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Email Composition */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="h-5 w-5" />
                    Compose Email
                  </CardTitle>
                  <CardDescription>Write your email content</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject</Label>
                    <Input
                      id="subject"
                      placeholder="Enter email subject..."
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Message</Label>
                    <div className="border rounded-md">
                      <RichTextEditor
                        value={message}
                        onChange={setMessage}
                        placeholder="Write your email message here..."
                        height="300px"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Email Preview & Actions */}
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Email Summary</CardTitle>
                  <CardDescription>
                    Review your email before sending
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Recipients:</span>
                      <span className="font-medium">
                        {getRecipientCount()} users
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Subject:</span>
                      <span className="font-medium truncate ml-2">
                        {subject || "No subject"}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">
                        Message length:
                      </span>
                      <span className="font-medium">
                        {message.length} chars
                      </span>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-2">
                    <h4 className="text-sm font-medium">Preview:</h4>
                    <div className="p-3 bg-muted rounded-md text-sm">
                      <div className="font-medium mb-2">
                        {subject || "No subject"}
                      </div>
                      <div
                        className="text-muted-foreground prose prose-sm max-w-none dark:prose-invert"
                        dangerouslySetInnerHTML={{
                          __html: message || "No message content",
                        }}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label
                      htmlFor="preview-email"
                      className="text-sm font-medium"
                    >
                      Preview Email Address (Optional)
                    </Label>
                    <Input
                      id="preview-email"
                      type="email"
                      placeholder="Enter email address to test preview..."
                      value={previewEmail}
                      onChange={(e) => setPreviewEmail(e.target.value)}
                      className="text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      Leave empty to use default admin email
                    </p>
                  </div>

                  <Button
                    onClick={handleSendPreview}
                    disabled={isLoading || !subject.trim() || !message.trim()}
                    className="w-full mb-2"
                    variant="outline"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2" />
                        Sending Preview...
                      </>
                    ) : (
                      <>
                        <Mail className="h-4 w-4 mr-2" />
                        Send Preview
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={handleSendMailWithRefresh}
                    disabled={isLoading || !subject.trim() || !message.trim()}
                    className="w-full"
                    size="lg"
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Email
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Quick Actions */}
              {/* <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start bg-transparent"
                onClick={() => {
                  setSubject("Welcome to GlowSkin!")
                  setMessage("Thank you for joining our community. We're excited to help you on your skincare journey!")
                }}
              >
                Welcome Template
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start bg-transparent"
                onClick={() => {
                  setSubject("New Product Launch!")
                  setMessage(
                    "We're thrilled to announce our latest skincare innovation. Check out our new products and special launch offers!",
                  )
                }}
              >
                Product Launch Template
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full justify-start bg-transparent"
                onClick={() => {
                  setSubject("Special Offer Just for You")
                  setMessage("Don't miss out on our exclusive discount! Use code SAVE20 for 20% off your next order.")
                }}
              >
                Promotion Template
              </Button>
            </CardContent>
          </Card> */}
            </div>
          </div>
        </TabsContent>

        {/* Email Logs Tab */}
        <TabsContent value="logs" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Email Logs</CardTitle>
                  <CardDescription>
                    View and filter email sending history
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchEmailLogs}
                  disabled={logsLoading}
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-2 ${logsLoading ? "animate-spin" : ""}`}
                  />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filters */}
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <Label>Status</Label>
                  <Select
                    value={logsFilters.status || "all"}
                    onValueChange={(value) =>
                      setLogsFilters({ ...logsFilters, status: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="All Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="failed">Failed</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label>Email</Label>
                  <Input
                    placeholder="Search by email..."
                    value={logsFilters.email}
                    onChange={(e) =>
                      setLogsFilters({ ...logsFilters, email: e.target.value })
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setLogsPage(1);
                        fetchEmailLogs();
                      }
                    }}
                  />
                </div>
              </div>

              {/* Logs Table */}
              {logsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader />
                </div>
              ) : emailLogs.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No email logs found
                </div>
              ) : (
                <>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>ID</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Subject</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Error</TableHead>
                          <TableHead>Sent At</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {emailLogs.map((log) => (
                          <TableRow key={log.id}>
                            <TableCell className="font-mono text-sm">
                              {log.id}
                            </TableCell>
                            <TableCell>{log.email}</TableCell>
                            <TableCell className="max-w-[200px] truncate">
                              {log.subject || "—"}
                            </TableCell>
                            <TableCell>
                              <Badge
                                variant={
                                  log.status === "sent"
                                    ? "default"
                                    : log.status === "failed"
                                      ? "destructive"
                                      : "secondary"
                                }
                              >
                                {log.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="max-w-[200px] truncate text-sm text-muted-foreground">
                              {log.error || "—"}
                            </TableCell>
                            <TableCell className="text-sm">
                              {log.created_at
                                ? format(new Date(log.created_at), "PPp")
                                : "—"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {logsTotalPages > 1 && (
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() =>
                              logsPage > 1 && setLogsPage(logsPage - 1)
                            }
                            className={
                              logsPage === 1
                                ? "pointer-events-none opacity-50"
                                : "cursor-pointer"
                            }
                          />
                        </PaginationItem>
                        {Array.from(
                          { length: Math.min(5, logsTotalPages) },
                          (_, i) => {
                            const page = i + 1;
                            return (
                              <PaginationItem key={page}>
                                <PaginationLink
                                  onClick={() => setLogsPage(page)}
                                  isActive={logsPage === page}
                                  className="cursor-pointer"
                                >
                                  {page}
                                </PaginationLink>
                              </PaginationItem>
                            );
                          },
                        )}
                        <PaginationItem>
                          <PaginationNext
                            onClick={() =>
                              logsPage < logsTotalPages &&
                              setLogsPage(logsPage + 1)
                            }
                            className={
                              logsPage >= logsTotalPages
                                ? "pointer-events-none opacity-50"
                                : "cursor-pointer"
                            }
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  )}

                  <div className="text-sm text-muted-foreground text-center">
                    Showing {(logsPage - 1) * 20 + 1} to{" "}
                    {Math.min(logsPage * 20, logsTotal)} of {logsTotal} logs
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Email Analytics</CardTitle>
                  <CardDescription>
                    Performance metrics grouped by email subject
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchEmailAnalytics}
                  disabled={analyticsLoading}
                >
                  <RefreshCw
                    className={`h-4 w-4 mr-2 ${analyticsLoading ? "animate-spin" : ""}`}
                  />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {analyticsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader />
                </div>
              ) : analytics.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  No analytics data available
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Subject</TableHead>
                        <TableHead>Total</TableHead>
                        <TableHead>Sent</TableHead>
                        <TableHead>Failed</TableHead>
                        <TableHead>Pending</TableHead>
                        <TableHead>Success Rate</TableHead>
                        <TableHead>First Sent</TableHead>
                        <TableHead>Last Sent</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {analytics.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium max-w-[250px] truncate">
                            {item.subject}
                          </TableCell>
                          <TableCell>{item.total}</TableCell>
                          <TableCell>
                            <Badge variant="default">{item.sent || 0}</Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="destructive">
                              {item.failed || 0}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">
                              {item.pending || 0}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <span className="font-medium">
                                {item.successRate || "0.00"}%
                              </span>
                              <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-primary"
                                  style={{ width: `${item.successRate || 0}%` }}
                                />
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">
                            {item.first_sent
                              ? format(new Date(item.first_sent), "PPp")
                              : "—"}
                          </TableCell>
                          <TableCell className="text-sm">
                            {item.last_sent
                              ? format(new Date(item.last_sent), "PPp")
                              : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
